<script>
import { required } from "vuelidate/lib/validators";
import validationMessages from "@/components/validations";
import Monitor from "@/services/Monitor";
import Swal from "sweetalert2";

export default {
  components: { validationMessages },

  props: {
    tournamentOptions: {
      type: Array,
      default: () => [],
    },
    provider: {
      type: String,
      default: "Azerion",
    },
  },

  data() {
    return {
      tournament_id: "",
      stage_name: "",
      stage_start: "",
      stage_end: "",
      stage_id: "",
      submitted: false,
      tryingToSubmit: false,
    };
  },

  validations: {
    tournament_id: { required },
    stage_name: { required },
    stage_start: { required },
    stage_end: { required },
  },

  methods: {
    closeModal() {
      this.$bvModal.hide("add_sport_stage");
    },

    formatError(error) {
      if (!error) return "Failed to create stage";
      if (Array.isArray(error)) return error.join(", ");
      return error;
    },

    async createSportStage() {
      this.tryingToSubmit = true;
      this.submitted = true;
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.tryingToSubmit = false;
        return;
      }

      const payload = {
        tournament_id: this.tournament_id,
        stage_name: this.stage_name,
        stage_start: this.stage_start,
        stage_end: this.stage_end,
        provider: this.provider,
      };

      if (this.stage_id) payload.stage_id = this.stage_id;

      try {
        await Monitor.createSportStage(payload);
        Swal.fire("Success!", "Stage created successfully!", "success");
        this.closeModal();
        this.$emit("onRefresh");
      } catch (error) {
        const err = error.response && error.response.data && error.response.data.error
          ? error.response.data.error
          : "";
        Swal.fire("Fail!", this.formatError(err), "warning");
      } finally {
        this.tryingToSubmit = false;
      }
    },

    resetForm() {
      this.tournament_id = "";
      this.stage_name = "";
      this.stage_start = "";
      this.stage_end = "";
      this.stage_id = "";
      this.submitted = false;
      this.$v.$reset();
    },
  },
};
</script>

<template>
  <b-modal
    id="add_sport_stage"
    title="Add Stage"
    title-class="font-18"
    centered
    size="lg"
    @hidden="resetForm"
  >
    <form @submit.prevent="createSportStage">
      <div class="form-group mb-3">
        <label for="stage_tournament_id">Tournament <span class="text-danger">*</span></label>
        <b-form-select
          id="stage_tournament_id"
          v-model="tournament_id"
          :class="{ 'is-invalid': submitted && $v.tournament_id.$error }"
        >
          <option value="">Select tournament</option>
          <option
            v-for="item in tournamentOptions"
            :key="item.tournament_id"
            :value="item.tournament_id"
          >
            {{ item.tournament_name }} — {{ item.tournament_season }} ({{ item.competition_name }})
          </option>
        </b-form-select>
        <validationMessages v-if="submitted" :field="$v.tournament_id" />
      </div>

      <div class="form-group mb-3">
        <label for="stage_name">Stage Name <span class="text-danger">*</span></label>
        <input
          id="stage_name"
          v-model="stage_name"
          type="text"
          class="form-control"
          :class="{ 'is-invalid': submitted && $v.stage_name.$error }"
        />
        <validationMessages v-if="submitted" :field="$v.stage_name" />
      </div>

      <div class="row">
        <div class="col-md-6">
          <div class="form-group mb-3">
            <label for="stage_start">Start Date <span class="text-danger">*</span></label>
            <input
              id="stage_start"
              v-model="stage_start"
              type="date"
              class="form-control"
              :class="{ 'is-invalid': submitted && $v.stage_start.$error }"
            />
            <validationMessages v-if="submitted" :field="$v.stage_start" />
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group mb-3">
            <label for="stage_end">End Date <span class="text-danger">*</span></label>
            <input
              id="stage_end"
              v-model="stage_end"
              type="date"
              class="form-control"
              :class="{ 'is-invalid': submitted && $v.stage_end.$error }"
            />
            <validationMessages v-if="submitted" :field="$v.stage_end" />
          </div>
        </div>
      </div>

      <div class="form-group mb-3">
        <label for="stage_id">Stage ID</label>
        <input id="stage_id" v-model="stage_id" type="text" class="form-control" placeholder="Auto-generated if empty" />
      </div>
    </form>

    <template v-slot:modal-footer>
      <b-button variant="light" @click="closeModal">Close</b-button>
      <b-button variant="primary" @click="createSportStage" :disabled="tryingToSubmit">
        <b-spinner v-show="tryingToSubmit" small></b-spinner>
        Save
      </b-button>
    </template>
  </b-modal>
</template>
